import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';

/**
 * Barra flutuante de confirmação/descarte (chair UI).
 *
 * Tem um único "lar" — o slot ao fim do conteúdo, dentro do flow. Quando
 * `dirty` é true E o lar está fora do viewport, a barra "decola" para
 * position:fixed no bottom do viewport (com shadow). Quando volta a ser
 * pristine (save/discard) OU o usuário scrolla até o lar, ela "pousa" de volta.
 *
 * Transições usam FLIP (First-Last-Invert-Play) via Web Animations API:
 * medimos a posição visual antes/depois da troca de classe e animamos o
 * transform do delta inverso até 0. Visual-target cap mantém a amplitude
 * visível perceptível mesmo quando o lar está longe do viewport.
 *
 * Estado de "o que é dirty / como salvar/descartar" fica na página
 * consumidora — este componente só recebe `[dirty]`/`[loading]` e emite
 * `(save)`/`(discard)`.
 */
@Component({
  selector: 'app-floating-submit-bar',
  templateUrl: './floating-submit-bar.component.html',
  styleUrls: ['./floating-submit-bar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FloatingSubmitBarComponent implements OnChanges, OnDestroy {
  @Input() dirty = false;
  @Input() loading = false;
  // Quando true, a barra projeta `<ng-content>` (botões custom) em vez do par
  // padrão salvar/descartar. A lógica de interação (decolar/pousar/FLIP) é a mesma.
  @Input() custom = false;
  @Input() saveLabel = 'forms.buttons.save-changes';
  @Input() savingLabel = 'forms.buttons.saving';
  @Input() discardLabel = 'forms.buttons.discard';

  // Modo de "decolagem" — quando a barra entra em estado fixed (away from home).
  // O critério é avaliado JUNTO COM a visibilidade do lar; a barra só decola se
  // (modo permite) E (lar fora do viewport).
  //   - 'always'       → decola sempre que sair de vista, mesmo sem dirty
  //   - 'at_least'     → decola se `dirty` for true (DEFAULT — consumidor seta
  //                      dirty=true quando QUALQUER item da barra estiver ativo)
  //   - 'conservative' → decola se `dirty` for true (consumidor seta dirty=true
  //                      apenas quando há SELEÇÃO na tabela associada). Mesmo
  //                      comportamento técnico do `at_least`; o nome reflete a
  //                      política do consumidor.
  //   - 'never'        → NUNCA decola; fica sempre presa ao lar
  @Input() floatingMode: 'always' | 'at_least' | 'conservative' | 'never' = 'at_least';

  @Output() save = new EventEmitter<void>();
  @Output() discard = new EventEmitter<void>();

  awayFromHome = false;
  private naturalPositionVisible = false;
  private sentinelObserver?: IntersectionObserver;
  private sentinelEl?: HTMLElement;
  private scrollRoot: Element | null = null;
  private currentAnim?: Animation;
  private static readonly FLIP_DURATION_MS = 250;
  // Duração da VOLTA AO LAR — global, vale para todos os consumidores da barra.
  private static readonly LANDING_DURATION_MS = 250;

  // Marca se a barra já fez seu PRIMEIRO update REAL com geometria válida.
  // Detecta "aba inativa" (sentinel com dimensão zero porque ancestral é
  // `display:none`) e mantém `hasSettled=false` durante esse estado.
  // Quando a aba é ativada (sentinel ganha dimensão), o próximo
  // updateAwayState faz SNAP sem animação, e DAÍ marca hasSettled=true.
  // Resultado: cada vez que uma aba "aparece" pela primeira vez, ela snap-a
  // ao estado correto SEM animar; só user-interactions subsequentes animam.
  private hasSettled = false;

  @ViewChild('submitActions') submitActionsEl?: ElementRef<HTMLElement>;
  @ViewChild('submitBlurBand') submitBlurBandEl?: ElementRef<HTMLElement>;
  // O slot permanece NO FLUXO mesmo com a barra flutuando (tem `min-height`
  // reservado), então é ele a referência de onde o lar está agora.
  @ViewChild('submitSlot') submitSlotEl?: ElementRef<HTMLElement>;

  // Setter pattern: o sentinel pode viver dentro de um *ngIf da página
  // consumidora, então monta-se o observer quando o elemento aparece.
  // rootMargin bottom 8px cria histerese — evita o flick perpétuo quando o
  // sentinel cai exatamente no edge inferior do viewport.
  @ViewChild('submitSentinel') set submitSentinel(el: ElementRef<HTMLElement> | undefined) {
    this.sentinelEl = el?.nativeElement;
    if (el?.nativeElement && !this.sentinelObserver) {
      // Root = container de scroll mais próximo (não a viewport). Crucial quando o "lar"
      // vive DENTRO de um elemento com scroll próprio (ex.: a barra de ações dentro do card
      // da lista de submissões): a visibilidade do lar precisa ser relativa a esse container.
      // Sem container de scroll (página comum), root = null = viewport.
      this.scrollRoot = this.getScrollRoot(el.nativeElement);
      // O observer só DISPARA a reavaliação em mudanças de interseção (scroll/layout); o
      // valor de `naturalPositionVisible` é recalculado pela GEOMETRIA em updateAwayState,
      // pra não depender do estado guardado pelo observer (que fica defasado no estado
      // inicial e em mudanças de layout sem scroll) — assim a barra decola já na seleção.
      this.sentinelObserver = new IntersectionObserver(
        () => this.updateAwayState(),
        { root: this.scrollRoot, threshold: 0, rootMargin: '0px 0px 8px 0px' }
      );
      this.sentinelObserver.observe(el.nativeElement);
    }
  }

  // Sobe a árvore procurando o primeiro ancestral que REALMENTE rola na vertical
  // (overflow-y auto/scroll E scrollHeight > clientHeight). Ignora ancestrais
  // `overflow:auto` que na prática NÃO rolam — ex.: o `mat-sidenav-content`, cujo
  // scroll real é do documento: ancorar o observer nele não dispararia no scroll
  // (ele se move junto com a página). Sem container próprio que role → `null`
  // (viewport), o que faz o observer disparar no scroll do documento.
  private getScrollRoot(el: HTMLElement): Element | null {
    let node = el.parentElement;
    while (node && node !== document.documentElement && node !== document.body) {
      const oy = getComputedStyle(node).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone) {
    // Fora da zona: redimensionar não deve disparar detecção de mudanças —
    // aqui só se escreve um `left` inline, nenhum estado do Angular muda.
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onResize, { passive: true });
    });
  }

  /**
   * O `left` da barra flutuante é copiado do lar UMA VEZ, na decolagem. Se a
   * janela muda de largura enquanto ela está fora do lar, o lar se move e a
   * barra fica para trás — daí a recolocação.
   *
   * Custo: um `getBoundingClientRect` por quadro de resize, e só enquanto
   * flutuando. Coalescido por rAF porque o resize dispara em rajada, e feito
   * na fase de leitura antes da escrita para não intercalar layout com estilo.
   */
  private onResize = () => {
    if (!this.awayFromHome || this.resizeFrame !== null) { return; }
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.realignToHome();
    });
  };

  private resizeFrame: number | null = null;

  /**
   * Recoloca a barra na horizontal do lar. O lar é o centro do slot — no
   * fluxo, a barra tem `width: fit-content; margin: 0 auto` dentro dele —,
   * então a conta reproduz essa centralização com a barra em `fixed`.
   */
  private realignToHome(): void {
    const el = this.submitActionsEl?.nativeElement;
    const slot = this.submitSlotEl?.nativeElement;
    if (!el || !slot || !this.awayFromHome) { return; }

    const slotRect = slot.getBoundingClientRect();
    const barWidth = el.getBoundingClientRect().width;
    if (slotRect.width === 0) { return; }

    el.style.left = `${slotRect.left + (slotRect.width - barWidth) / 2}px`;
    el.style.right = 'auto';

    // Sincroniza a banda de vidro fosco com o rect atual do bar — largura
    // da banda = largura do bar (+ zona de fade lateral pra suavizar as
    // pontas). Centraliza usando a mesma coordenada `left` da barra.
    this.syncBlurBandToBar();
  }

  /** Fade lateral (px) — largura extra em cada lado da banda pra que o
   *  fade horizontal (via mask) tenha espaço pra suavizar sem cortar o
   *  blur exatamente na borda do bar. */
  private static readonly BLUR_BAND_SIDE_FADE_PX = 48;

  /** Sincroniza left/width da banda de blur com o rect atual do bar
   *  (+ zona de fade). Chamado ao alinhar/decolar/pousar. */
  private syncBlurBandToBar(): void {
    const bar = this.submitActionsEl?.nativeElement;
    const band = this.submitBlurBandEl?.nativeElement;
    if (!bar || !band) { return; }
    const rect = bar.getBoundingClientRect();
    if (rect.width === 0) { return; }
    const fade = FloatingSubmitBarComponent.BLUR_BAND_SIDE_FADE_PX;
    band.style.left = `${rect.left - fade}px`;
    band.style.width = `${rect.width + fade * 2}px`;
    band.style.right = 'auto';
    band.style.marginLeft = '0';
    band.style.marginRight = '0';
  }

  /** Limpa inline do sync — chamado ao pousar (banda some via `:has()`
   *  anyway, mas limpamos pra não guardar dimensões passadas). */
  private resetBlurBand(): void {
    const band = this.submitBlurBandEl?.nativeElement;
    if (!band) { return; }
    band.style.left = '';
    band.style.width = '';
    band.style.right = '';
    band.style.marginLeft = '';
    band.style.marginRight = '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.dirty || changes.floatingMode) {
      this.updateAwayState();
    }
  }

  // Política de decolagem por modo:
  //   - always       → decola sempre
  //   - at_least     → "ALGUM item da barra está ativo"
  //                    • !custom (par padrão save/discard): respeita `dirty`
  //                      (form tem alterações pendentes = save está "ativo")
  //                    • custom (botões projetados via ng-content): o próprio
  //                      fato de a barra existir implica que pelo menos 1 item
  //                      está renderizado/ativo → decola
  //   - conservative → respeita `dirty` (consumidor sinaliza algo específico,
  //                    ex.: seleção em tabela). Mesma implementação técnica
  //                    do at_least no modo save/discard, semântica distinta.
  //   - never        → nunca decola
  private get modeAllowsFloating(): boolean {
    switch (this.floatingMode) {
      case 'always': return true;
      case 'never': return false;
      case 'at_least': return this.custom ? true : this.dirty;
      case 'conservative':
      default: return this.dirty;
    }
  }

  ngOnDestroy() {
    this.sentinelObserver?.disconnect();
    this.currentAnim?.cancel();
    window.removeEventListener('resize', this.onResize);
    if (this.resizeFrame !== null) { cancelAnimationFrame(this.resizeFrame); }
  }

  onSave() {
    this.save.emit();
  }

  onDiscard() {
    this.discard.emit();
  }

  private updateAwayState() {
    const sentinel = this.sentinelEl;
    if (!sentinel) { return; }

    const sr = sentinel.getBoundingClientRect();

    // Sentinel com dimensão zero (width=0 E height=0): a barra está dentro
    // de um ancestral `display:none` (típico: chair-tab inativa). Resetamos
    // hasSettled — quando a aba for ativada, o próximo update faz SNAP sem
    // animação. Sem esse reset, o user vê a animação FLIP toda vez que troca
    // pra uma aba cujo lar fica em posição diferente da última calculada.
    if (sr.width === 0 && sr.height === 0) {
      this.hasSettled = false;
      return;
    }

    // Área visível do "lar" = INTERSEÇÃO do root de scroll com a viewport.
    let top = 0;
    let bottom = window.innerHeight;
    if (this.scrollRoot) {
      const rr = this.scrollRoot.getBoundingClientRect();
      top = Math.max(0, rr.top);
      bottom = Math.min(window.innerHeight, rr.bottom);
    }
    // Histerese de 8px embaixo, igual ao rootMargin do observer.
    this.naturalPositionVisible = sr.bottom > top && sr.top < bottom + 8;

    const shouldBeAway = this.modeAllowsFloating && !this.naturalPositionVisible;
    // POR QUE a barra está pousando decide se há viagem a animar. São dois
    // gatilhos distintos:
    //   • o modo deixou de permitir flutuar (dirty caiu) → o lar pode estar
    //     longe, há percurso real;
    //   • o lar entrou em vista (scroll) → a barra JÁ está encostada nele, por
    //     construção: o pouso dispara no instante em que o lar aparece.
    // No segundo caso não existe percurso; animar 250ms só prende a aparência
    // de flutuante numa barra que aos olhos já se encaixou.
    const byVisibility = !shouldBeAway && this.modeAllowsFloating;
    this.applyAwayState(shouldBeAway, byVisibility);
  }

  private applyAwayState(newValue: boolean, byVisibility = false) {
    // Não settled (sentinel acabou de ganhar dimensão = aba acabou de virar
    // ativa pela 1ª vez OU primeira renderização): aplica estado por SNAP,
    // sem FLIP animation. Depois, marca settled — interações subsequentes
    // do user animam normalmente.
    if (!this.hasSettled) {
      const el = this.submitActionsEl?.nativeElement;
      if (el) {
        this.awayFromHome = newValue;
        if (newValue) {
          // Captura a posição horizontal ATUAL (o "lar" já está renderizado
          // e centralizado). Usar essa coordenada como `left` no estado
          // fixed mantém visualmente alinhado.
          const rect = el.getBoundingClientRect();
          el.style.left = `${rect.left}px`;
          el.style.right = 'auto';
        } else {
          el.style.left = '';
          el.style.right = '';
        }
        this.cdr.detectChanges();
      } else {
        this.awayFromHome = newValue;
      }
      this.hasSettled = true;
      return;
    }

    if (this.awayFromHome === newValue) { return; }

    const el = this.submitActionsEl?.nativeElement;
    if (!el) {
      this.awayFromHome = newValue;
      return;
    }

    // Encaixe por scroll: sem percurso, sem animação. A classe sai e a
    // aparência acompanha no mesmo quadro — que é o que o olho já vê.
    if (byVisibility) {
      this.currentAnim?.cancel();
      this.currentAnim = undefined;
      this.awayFromHome = false;
      el.style.left = '';
      el.style.right = '';
      this.cdr.detectChanges();
      return;
    }

    // Duração do deslocamento. O CSS não tem mais transição alguma — a
    // aparência é instantânea nas pontas e fica fixa durante a viagem —, então
    // este é o único tempo que existe na barra.
    const duration = newValue
      ? FloatingSubmitBarComponent.FLIP_DURATION_MS
      : FloatingSubmitBarComponent.LANDING_DURATION_MS;

    // APARÊNCIA de flutuante, medida ANTES da troca de classe. Capturamos do
    // estilo computado em vez de repetir valores: sombra e borda são definidas
    // no tema (`_color-scheme.scss`, com par claro/escuro), e duplicá-las aqui
    // criaria uma segunda fonte para a mesma coisa.
    // Só sombra e cor da borda: o raio dos cantos é fixo no CSS (igual no lar e
    // flutuando), então não há o que segurar.
    const floatingLook = newValue ? null : {
      boxShadow: getComputedStyle(el).boxShadow,
      borderColor: getComputedStyle(el).borderColor,
      // Fundo também acompanha o pouso: a base é `transparent` (só no lar
      // é transparente) e o `.away-from-home` seta a cor sólida — sem
      // preservar aqui, a cor cairia no primeiro quadro do pouso e a barra
      // viajaria "vazia". Capturamos ANTES da troca de classe.
      backgroundColor: getComputedStyle(el).backgroundColor
    };

    const oldRect = el.getBoundingClientRect();
    this.awayFromHome = newValue;
    // Mantém o alinhamento HORIZONTAL do lar: usa a coordenada `left`
    // ATUAL (o "lar" já está renderizado e centralizado) como `left` fixed.
    // `margin: 0` no `.away-from-home` (SCSS) é CRÍTICO — sem ele, o
    // browser recomputa as margens auto da base e o `left` inline é
    // ignorado visualmente. Ao pousar, limpa inline e volta ao CSS.
    if (newValue) {
      el.style.left = `${oldRect.left}px`;
      el.style.right = 'auto';
      // Banda de blur passa a existir agora (via `:has()`); sincroniza
      // largura/left com o rect atual da barra.
      this.cdr.detectChanges();
      this.syncBlurBandToBar();
    } else {
      el.style.left = '';
      el.style.right = '';
      // ELEVAÇÃO durante o pouso. O `z-index: 10` mora na classe
      // `.away-from-home`, que a FLIP remove logo no INÍCIO do percurso — dali
      // em diante o elemento é estático, e `z-index` sequer se aplica a
      // elemento estático. O `transform` da animação cria contexto de
      // empilhamento próprio, mas equivalente a zero: a barra passa a perder
      // para qualquer conteúdo posicionado da página e viaja POR BAIXO dele.
      // `position: relative` é o que faz o `z-index` valer. Ambos são limpos ao
      // fim da animação, devolvendo o elemento ao que o CSS define.
      el.style.position = 'relative';
      el.style.zIndex = '10';

      // A aparência ACOMPANHA a viagem: sombra, borda e FUNDO seguem os do
      // estado flutuante até a barra chegar. Sem isto caem no primeiro quadro
      // (a classe já saiu) e a barra atravessa a página "descascada" — a
      // barra deve ser sólida DURANTE a viagem; só ao chegar no lar ela vira
      // transparente. `arrive()` abaixo limpa tudo no fim da animação.
      el.style.boxShadow = floatingLook.boxShadow;
      el.style.borderColor = floatingLook.borderColor;
      el.style.backgroundColor = floatingLook.backgroundColor;
    }
    this.cdr.detectChanges();
    const newRect = el.getBoundingClientRect();

    const viewportH = window.innerHeight;
    const visualOldTop = Math.min(oldRect.top, viewportH);
    const visualNewTop = Math.min(newRect.top, viewportH);

    const deltaX = oldRect.left - newRect.left;
    const startTranslateY = visualOldTop - newRect.top;
    const endTranslateY = visualNewTop - newRect.top;
    const scaleX = newRect.width === 0 ? 1 : oldRect.width / newRect.width;
    const scaleY = newRect.height === 0 ? 1 : oldRect.height / newRect.height;

    el.style.transformOrigin = '0 0';

    this.currentAnim?.cancel();
    this.currentAnim = el.animate(
      [
        { transform: `translate(${deltaX}px, ${startTranslateY}px) scale(${scaleX}, ${scaleY})` },
        { transform: `translate(0, ${endTranslateY}px) scale(1, 1)` }
      ],
      // ease-in-out: acelera na saída e desacelera na chegada. Diferente do
      // ease-out, o movimento termina JUNTO com o relógio da animação — não há
      // trecho final de rastejo em que a barra parece parada esperando o CSS.
      { duration, easing: 'ease-in-out' }
    );

    // Fim do pouso: devolve o empilhamento ao CSS. `onfinish` cobre a chegada
    // normal; `oncancel`, a interrupção por uma nova transição — sem os dois, a
    // barra ficaria elevada para sempre.
    if (!newValue) {
      // Chegada: devolve tudo ao CSS de uma vez. Não é preciso desligar
      // transição nenhuma — o SCSS não declara mais.
      const arrive = () => {
        el.style.position = '';
        el.style.zIndex = '';
        el.style.boxShadow = '';
        el.style.borderColor = '';
        el.style.backgroundColor = '';
        // Banda de blur some via `:has()` porque a classe .away-from-home saiu
        // no início da viagem; ainda assim, limpamos inline pra não guardar
        // dimensões antigas que aparecem na próxima decolagem antes do sync.
        this.resetBlurBand();
      };
      this.currentAnim.onfinish = arrive;
      this.currentAnim.oncancel = arrive;
    }
  }

}
